import type { Variants } from 'framer-motion';

/**
 * Shared landing-page motion vocabulary — a PatronEZ-toned port of the
 * peddlum primitives, with Japanese-B2B "informational motion" guardrails
 * baked in (TOPPAGE_REDESIGN.md → Motion budget):
 *
 *  - durations 0.4–0.6s (never >0.7); easing easeOutExpo, NO bounce/spring
 *    overshoot on entrances
 *  - small travel: y:16–18, x:24, scale:0.96–1 (peddlum's y:30/40 is too much)
 *  - every reveal fires ONCE (viewport once:true) — the only sanctioned
 *    perpetual loops on the page are the SignalTicker marquee + SignalRadar sweep
 *  - all variants are transform/opacity only so they respect prefers-reduced-
 *    motion when the caller passes `initial="visible"` (see useLandingMotion)
 *
 * Convention: parent gets `variants={staggerContainer} initial="hidden"
 * whileInView="visible" viewport={sectionViewport}`; children get a variant
 * (fadeInUp / scaleIn …) with NO explicit `animate` — the parent's stagger
 * drives them.
 */

export const EASE = [0.22, 1, 0.36, 1] as const;

export const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE } },
};

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.5, ease: EASE } },
};

export const fadeInLeft: Variants = {
  hidden: { opacity: 0, x: 24 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.55, ease: EASE } },
};

export const fadeInRight: Variants = {
  hidden: { opacity: 0, x: -24 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.55, ease: EASE } },
};

export const scaleIn: Variants = {
  hidden: { opacity: 0, scale: 0.96 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: EASE } },
};

export const blurIn: Variants = {
  hidden: { opacity: 0, filter: 'blur(6px)' },
  visible: { opacity: 1, filter: 'blur(0px)', transition: { duration: 0.55, ease: EASE } },
};

/** Origin-top connector line that draws once (use sparingly — one per viewport). */
export const drawLine: Variants = {
  hidden: { scaleY: 0 },
  visible: { scaleY: 1, transition: { duration: 1.1, ease: EASE } },
};

export const staggerContainer: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

export const staggerSlow: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.14 } },
};

/** Fire the reveal 60px before the element's top crosses into view, once. */
export const sectionViewport = { once: true, margin: '-60px' } as const;

/**
 * Props to spread onto a reveal container. When the user prefers reduced
 * motion, we render the visible state immediately (no transform) and skip
 * the whileInView trigger entirely.
 *
 *   const reveal = useReducedMotion();
 *   <motion.div {...revealProps(reveal)} variants={staggerContainer}>
 */
export function revealProps(reduced: boolean) {
  if (reduced) {
    return { initial: 'visible' as const, animate: 'visible' as const };
  }
  return {
    initial: 'hidden' as const,
    whileInView: 'visible' as const,
    viewport: sectionViewport,
  };
}
